import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface PillTab {
  id: string;
  label: string;
}

interface PillTabsProps {
  tabs: PillTab[];
  selectedId: string;
  onSelect: (id: string) => void;
  ariaLabel: string;
  className?: string;
}

/**
 * Horizontally scrolling row of btn-primary / btn-secondary pill tabs with
 * fade + arrow buttons when it overflows (same pattern as My Predictions).
 */
const PillTabs: React.FC<PillTabsProps> = ({ tabs, selectedId, onSelect, ariaLabel, className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [arrows, setArrows] = useState({ left: false, right: false });

  const checkScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const left = container.scrollLeft > 0;
    const right = container.scrollLeft + container.clientWidth < container.scrollWidth - 1;
    setArrows((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [tabs, checkScroll]);

  const scrollBy = (offset: number) => {
    containerRef.current?.scrollBy({ left: offset, behavior: 'smooth' });
    setTimeout(checkScroll, 300);
  };

  return (
    <div className={`overflow-hidden ${className}`}>
      <div className="relative min-w-0">
        {arrows.left && (
          <button
            type="button"
            aria-label="Scroll left"
            onClick={() => scrollBy(-200)}
            className="absolute left-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-r from-[#eff5f6] to-transparent dark:from-gray-900 dark:to-transparent flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        <div
          ref={containerRef}
          role="tablist"
          aria-label={ariaLabel}
          className="flex gap-2 overflow-x-auto scrollbar-hide min-w-0 pr-6"
          onScroll={checkScroll}
        >
          {tabs.map((tab) => {
            const isSelected = tab.id === selectedId;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isSelected}
                onClick={() => !isSelected && onSelect(tab.id)}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                  isSelected ? 'btn-primary' : 'btn-secondary'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {arrows.right && (
          <button
            type="button"
            aria-label="Scroll right"
            onClick={() => scrollBy(200)}
            className="absolute right-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-l from-[#eff5f6] to-transparent dark:from-gray-900 dark:to-transparent flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default PillTabs;
